import { dataTestId } from 'modules/experiments/constants';
import {
  useTakeoverExperimentContext,
  useVariantConfigurationContext,
} from 'modules/experiments/contexts';
import { getTakeoverCampaignMessageMedium } from 'modules/experiments/ducks/selectors';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { InAppContentDefinition, MESSAGE_FORMAT, MessageMedium } from 'modules/graphql';
import { SendTestMessage, useTestMessageOptions } from 'modules/templateEditor/common/Controls';
import {
  PREVIEW_HTML_TEMPLATE_URL,
  PREVIEW_IN_DEVICE_URL,
} from 'modules/templateEditor/common/constants';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useTemplateTakeover } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';
import { SendTestMsgModals } from 'modules/templateEditor/modals';
import { useSelector } from 'react-redux';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { CounterErrorBadge } from 'ui/badge';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button, SecondaryButton } from 'ui/button';
import { ChevronDown, Eye, OpenNewWindow16, Overflow } from 'ui/icons';
import { colors } from 'ui/theme';
import { NoOp } from 'utils/noop';

import {
  ButtonText,
  ComboButton,
  ContentActionOverflowMenu,
  ContentActionTriggerButton,
  ContentActionsWrapper,
  Divider,
  StyledActionMenu,
} from './Styled';

type ContentActionProps = {
  useActionMenu: boolean;
};

export type ContentActionComponentProps = {
  isNotControlVariant: boolean;
  medium: MessageMedium;
  handleTemplateEdit: NoOp;
  handleTemplatePreview: NoOp;
};

type PreviewButtonProps = {
  handleTemplatePreview: NoOp;
};

type ContentActionMenuOptions = ActionMenuOption & { isVisible?: boolean };

const useEmailPreviewContentOptions = () => {
  const { t } = useExperimentsTranslations();
  const { liveTemplate } = useEditableFieldsContext();
  const { mediumData = {} } = liveTemplate || {};
  const { messageFormat } = mediumData as InAppContentDefinition;
  const { selectedVariant } = useVariantConfigurationContext();

  const previewOnDeviceUrl = `${PREVIEW_IN_DEVICE_URL}?templateId=${selectedVariant?.id}`;
  const previewHTMLUrl = `${PREVIEW_HTML_TEMPLATE_URL}?templateId=${selectedVariant?.id}`;
  const isJSON = messageFormat === MESSAGE_FORMAT.Json;

  const options: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Link,
      content: (
        <>
          {t('previewActions.previewInNewTab')}
          <OpenNewWindow16 />
        </>
      ),
      id: 'btn-edit-design-preview-in-new-tab',
      href: previewHTMLUrl,
      target: '_blank',
      isExternalLink: true,
    },
    {
      as: AllowedActionMenuElements.Link,
      content: (
        <>
          {t('previewActions.previewOnDevice')}
          <OpenNewWindow16 />
        </>
      ),
      id: 'btn-edit-design-preview-on-devices',
      href: previewOnDeviceUrl,
      target: '_blank',
      isExternalLink: true,
    },
  ];

  return {
    isPreviewButtonDisabled: isJSON,
    options,
  };
};

const PreviewButton = ({ handleTemplatePreview }: PreviewButtonProps) => {
  const { t } = useExperimentsTranslations();

  return (
    <SecondaryButton
      data-test="template-editor-preview-button"
      size={BUTTON_SIZES.SMALL}
      onClick={handleTemplatePreview}
    >
      <Eye height={16} width={16} />
      <ButtonText>{t('previewActions.preview')}</ButtonText>
    </SecondaryButton>
  );
};

export const PreviewContentEmailButton = ({ handleTemplatePreview }: PreviewButtonProps) => {
  const { options, isPreviewButtonDisabled } = useEmailPreviewContentOptions();

  return (
    <ComboButton>
      <PreviewButton handleTemplatePreview={handleTemplatePreview} />
      <Divider />
      <StyledActionMenu align={MENU_ALIGNMENTS.RIGHT} options={options}>
        <Button
          isDisabled={isPreviewButtonDisabled}
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SECONDARY}
        >
          <ChevronDown data-test="email-preview-combo-button" height={16} width={16} />
        </Button>
      </StyledActionMenu>
    </ComboButton>
  );
};

export const ContentActionButtons = ({
  isNotControlVariant,
  medium,
  handleTemplatePreview,
  handleTemplateEdit,
}: ContentActionComponentProps) => {
  const { t } = useExperimentsTranslations();
  const { selectedVariantErrorCount } = useVariantConfigurationContext();

  return (
    <>
      <SendTestMessage buttonSize={BUTTON_SIZES.SMALL} />
      {medium === MessageMedium.Email && (
        <PreviewContentEmailButton handleTemplatePreview={handleTemplatePreview} />
      )}
      {medium !== MessageMedium.Email && (
        <PreviewButton handleTemplatePreview={handleTemplatePreview} />
      )}
      {isNotControlVariant && (
        <SecondaryButton
          data-test="template-editor-button"
          size={BUTTON_SIZES.SMALL}
          onClick={handleTemplateEdit}
        >
          {t('previewActions.edit')}
          {selectedVariantErrorCount > 0 && (
            <CounterErrorBadge>{selectedVariantErrorCount}</CounterErrorBadge>
          )}
        </SecondaryButton>
      )}
    </>
  );
};

export const ContentActionOverflowMenuAction = ({
  isNotControlVariant,
  medium,
  handleTemplatePreview,
  handleTemplateEdit,
}: ContentActionComponentProps) => {
  const { t } = useExperimentsTranslations();
  const { options: sendTestMessageOptions, isSendTestMessageButtonDisabled } =
    useTestMessageOptions();
  const { options: emailPreviewOptions, isPreviewButtonDisabled } = useEmailPreviewContentOptions();

  const contentActionMenuOptions: ContentActionMenuOptions[] = [
    {
      id: 'content-action-edit',
      as: AllowedActionMenuElements.Button,
      content: t('previewActions.edit'),
      onClick: handleTemplateEdit,
      isVisible: isNotControlVariant,
    },
    {
      id: 'content-action-send-proof-title',
      as: AllowedActionMenuElements.Title,
      content: t('previewActions.sendProof'),
    },
    ...sendTestMessageOptions.map((sendProofOption) => ({
      ...sendProofOption,
      isDisabled: isSendTestMessageButtonDisabled,
      isVisible: true,
    })),
    {
      id: 'content-action-preview-title',
      as: AllowedActionMenuElements.Title,
      content: t('previewActions.preview'),
    },
    {
      id: 'content-action-preview-in-tab',
      as: AllowedActionMenuElements.Button,
      content: t('previewActions.previewInThisTab'),
      onClick: handleTemplatePreview,
    },
    ...emailPreviewOptions.map((sendProofOption) => ({
      ...sendProofOption,
      isDisabled: isPreviewButtonDisabled,
      isVisible: medium === MessageMedium.Email,
    })),
  ].filter(({ isVisible = true }) => isVisible);

  return (
    <ContentActionOverflowMenu align={MENU_ALIGNMENTS.RIGHT} options={contentActionMenuOptions}>
      <ContentActionTriggerButton size={BUTTON_SIZES.SMALL} onClick={() => null}>
        <Overflow data-test={dataTestId.contentActionMenu} color={colors.secondaryText} />
      </ContentActionTriggerButton>
    </ContentActionOverflowMenu>
  );
};

export const ContentActions = ({ useActionMenu }: ContentActionProps) => {
  const {
    unsavedChanges: { variantList: unsavedVariant, unsavedVariantModal },
  } = useTakeoverExperimentContext();
  const { selectedVariant } = useVariantConfigurationContext();
  const takeover = useTemplateTakeover();
  const openTemplateTakeover = () => takeover.open(takeover.viewOptions.settings);
  const handleTemplateEdit = () =>
    unsavedVariant ? unsavedVariantModal(openTemplateTakeover) : openTemplateTakeover();
  const handleTemplatePreview = () =>
    takeover.set({ view: takeover.viewOptions.preview, isOpen: true });
  const isNotControlVariant = !selectedVariant?.control;
  const medium = useSelector(getTakeoverCampaignMessageMedium);

  return (
    <ContentActionsWrapper>
      {useActionMenu ? (
        <ContentActionOverflowMenuAction
          isNotControlVariant={isNotControlVariant}
          medium={medium}
          handleTemplatePreview={handleTemplatePreview}
          handleTemplateEdit={handleTemplateEdit}
        />
      ) : (
        <ContentActionButtons
          isNotControlVariant={isNotControlVariant}
          medium={medium}
          handleTemplatePreview={handleTemplatePreview}
          handleTemplateEdit={handleTemplateEdit}
        />
      )}
      <SendTestMsgModals />
    </ContentActionsWrapper>
  );
};
